// packages/web-ui/src/components/slash-palette/SlashContent.tsx
//
// Story W2-S7 AC #7 — shared list + parameter-form surface for both the
// inline `<SlashMenu>` and the modal `<GlobalPalette>`. The two host shells
// only differ in chrome — the actual command list, search input, and
// parameter-form transitions live here.

import type { ChangeEvent, JSX, KeyboardEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  buildPreview,
  defaultParamsFor,
  filterCommands,
  findCommand,
  SLASH_COMMANDS,
  type SlashCommand,
  type SlashDispatchContext,
} from './commands.js';
import { ParameterForm } from './parameter-form.js';

export interface SlashContentProps {
  /** Mode: 'inline' renders without an explicit search bar (composer-fed query);
   *  'modal' adds a top-level input + section headings. */
  mode: 'inline' | 'modal';
  /** Search query — for inline mode this is the textarea content sans `/`. */
  query: string;
  /** Setter for the modal-mode search field (ignored in inline mode). */
  onQueryChange?: (value: string) => void;
  /** Dispatch collaborator surface. */
  ctx: SlashDispatchContext;
  /** Fires when the menu should close (after a command runs or Esc). */
  onClose: () => void;
  /** Initial selected command id when the parent forces parameter-form mode. */
  initialCommandId?: string;
  /** When true, auto-jumps to parameter form for the first matched command. */
  autoOpenFirstParams?: boolean;
  /**
   * Command source — defaults to the static `SLASH_COMMANDS` table. Hosts
   * pass `[...SLASH_COMMANDS, ...buildSkillCommands(skills)]` to merge in
   * per-skill `/<skill-id>` entries.
   */
  commands?: ReadonlyArray<SlashCommand>;
}

/** Phase machine for the menu list view → parameter-form view. */
type Phase =
  | { kind: 'list'; activeIndex: number }
  | { kind: 'params'; commandId: string; values: Record<string, unknown> };

export function SlashContent(props: SlashContentProps): JSX.Element {
  const { mode, query, onQueryChange, ctx, onClose, commands = SLASH_COMMANDS } = props;

  const filtered = useMemo<SlashCommand[]>(
    () => filterCommands(query, commands),
    [query, commands],
  );

  const initialPhase: Phase = (() => {
    if (props.initialCommandId !== undefined) {
      const cmd = findCommand(props.initialCommandId, commands);
      if (cmd !== undefined) {
        return { kind: 'params', commandId: cmd.id, values: defaultParamsFor(cmd) };
      }
    }
    return { kind: 'list', activeIndex: 0 };
  })();

  const [phase, setPhase] = useState<Phase>(initialPhase);
  const listRef = useRef<HTMLUListElement | null>(null);

  // Reset active index when the filtered list shrinks below it.
  useEffect(() => {
    if (phase.kind === 'list' && phase.activeIndex >= filtered.length) {
      setPhase({ kind: 'list', activeIndex: 0 });
    }
  }, [filtered, phase]);

  // Auto-open params when the parent requests it AND there is exactly one match.
  useEffect(() => {
    if (props.autoOpenFirstParams === true && phase.kind === 'list' && filtered.length === 1) {
      const only = filtered[0];
      if (only !== undefined && only.parameters.length > 0) {
        setPhase({ kind: 'params', commandId: only.id, values: defaultParamsFor(only) });
      }
    }
  }, [props.autoOpenFirstParams, filtered, phase.kind]);

  const runCommand = (command: SlashCommand, params: Record<string, unknown>): void => {
    void Promise.resolve(command.dispatch(params, ctx)).finally(() => {
      onClose();
    });
  };

  const enterParamsForCommand = (cmd: SlashCommand): void => {
    if (cmd.parameters.length === 0) {
      runCommand(cmd, {});
      return;
    }
    setPhase({ kind: 'params', commandId: cmd.id, values: defaultParamsFor(cmd) });
  };

  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (phase.kind !== 'list') return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = filtered.length === 0 ? 0 : Math.min(phase.activeIndex + 1, filtered.length - 1);
      setPhase({ kind: 'list', activeIndex: next });
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const next = Math.max(phase.activeIndex - 1, 0);
      setPhase({ kind: 'list', activeIndex: next });
      return;
    }
    if (event.key === 'Enter') {
      const cmd = filtered[phase.activeIndex];
      if (cmd === undefined) return;
      event.preventDefault();
      enterParamsForCommand(cmd);
      return;
    }
    if (event.key === 'ArrowRight') {
      const cmd = filtered[phase.activeIndex];
      if (cmd === undefined) return;
      if (cmd.parameters.length > 0) {
        event.preventDefault();
        setPhase({ kind: 'params', commandId: cmd.id, values: defaultParamsFor(cmd) });
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  const handleParamsKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (phase.kind !== 'params') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === 'Enter' && event.ctrlKey) {
      const cmd = findCommand(phase.commandId, commands);
      if (cmd === undefined) return;
      event.preventDefault();
      runCommand(cmd, defaultParamsFor(cmd));
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onQueryChange?.(event.target.value);
  };

  const renderList = (): JSX.Element => (
    <div onKeyDown={handleListKeyDown} role="presentation">
      {filtered.length === 0 ? (
        <div
          data-testid="slash-empty"
          className="px-3 py-2 text-[12px] text-taupe-700"
        >
          No matching commands
        </div>
      ) : (
        <ul
          ref={listRef}
          role="listbox"
          aria-label="Slash commands"
          data-testid="slash-list"
          className="flex max-h-72 flex-col overflow-y-auto"
        >
          {filtered.map((cmd, idx) => {
            const isActive = phase.kind === 'list' && idx === phase.activeIndex;
            const Icon = cmd.icon;
            return (
              <li
                key={cmd.id}
                role="option"
                aria-selected={isActive}
                data-testid="slash-row"
                data-command-id={cmd.id}
                data-active={isActive ? 'true' : 'false'}
                tabIndex={-1}
                onMouseEnter={() => {
                  setPhase({ kind: 'list', activeIndex: idx });
                }}
                onClick={() => {
                  enterParamsForCommand(cmd);
                }}
                className={[
                  'flex cursor-pointer items-center gap-2 px-3 py-1 text-[12px]',
                  isActive
                    ? 'bg-[var(--accent-cream-active)] text-taupe-900'
                    : 'hover:bg-cream-200',
                ].join(' ')}
              >
                <Icon aria-hidden="true" className="h-3 w-3 shrink-0 text-taupe-700" />
                <span className="font-medium">{cmd.label}</span>
                <span className="ml-auto truncate text-[11px] text-taupe-700">
                  {cmd.description}
                </span>
                {cmd.stub === true ? (
                  <span
                    data-testid="slash-stub-badge"
                    className="border-taupe-500 text-taupe-700 ml-1 rounded-none border px-1 text-[10px] font-semibold uppercase tracking-wide"
                  >
                    {cmd.stubStory}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  const renderParams = (): JSX.Element | null => {
    if (phase.kind !== 'params') return null;
    const cmd = findCommand(phase.commandId, commands);
    if (cmd === undefined) return null;
    const handleParamChange = (name: string, value: unknown): void => {
      setPhase({
        kind: 'params',
        commandId: phase.commandId,
        values: { ...phase.values, [name]: value },
      });
    };
    const handleRun = (): void => {
      runCommand(cmd, phase.values);
    };
    const handleCancel = (): void => {
      setPhase({ kind: 'list', activeIndex: 0 });
    };
    return (
      <div onKeyDown={handleParamsKeyDown}>
        <ParameterForm
          command={cmd}
          values={phase.values}
          onChange={handleParamChange}
          onRun={handleRun}
          onCancel={handleCancel}
        />
        <span data-testid="slash-preview-mirror" className="hidden">
          {buildPreview(cmd, phase.values)}
        </span>
      </div>
    );
  };

  return (
    <div
      data-testid={mode === 'inline' ? 'slash-menu-content' : 'global-palette-content'}
      data-mode={mode}
      className="flex w-[360px] flex-col"
    >
      {mode === 'modal' ? (
        <input
          autoFocus
          type="text"
          data-testid="global-palette-search"
          aria-label="Search commands"
          placeholder="Search commands…"
          value={query}
          onChange={handleSearchChange}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              onClose();
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
              handleListKeyDown(event as unknown as KeyboardEvent<HTMLDivElement>);
            }
          }}
          className="border-b border-taupe-300 bg-cream-100 px-3 py-2 text-[13px] text-taupe-900 outline-none focus:bg-cream-200"
        />
      ) : null}

      {phase.kind === 'list' ? renderList() : renderParams()}
    </div>
  );
}

/** Test-only export: full v1 command set. Useful for assertions. */
export const _ALL_COMMANDS = SLASH_COMMANDS;
